import React from 'react';
import { Modal } from 'react-bootstrap';
import { ModalKey } from '@dreamai/types';
import { useOnboardingModals } from '../../../providers';
import { ReactComponent as AgentCardIllustration } from '../../../icons/spending-onboarding-agent-card.svg';
import { ReactComponent as UserCardIllustration } from '../../../icons/spending-onboarding-user-card.svg';
import './spending-onboarding-modal.scss';

interface SpendingOnboardingModalProps {
  show: boolean;
  onNext: () => void;
  agentCardOnly: boolean;
  agentImageUrl?: string;
  explainDeposit?: boolean;
}

const SpendingOnboardingModal: React.FC<SpendingOnboardingModalProps> = ({
  show,
  onNext,
  agentCardOnly,
  agentImageUrl,
  explainDeposit,
}) => {
  const { markAsSeen } = useOnboardingModals();

  const modalKey: ModalKey = explainDeposit
    ? 'deposit_onboarding'
    : agentCardOnly
      ? 'spending_onboarding_agent_only'
      : 'spending_onboarding_both';

  const handleNext = async () => {
    await markAsSeen(modalKey);
    onNext();
  };

  return (
    <Modal
      show={show}
      centered
      backdrop='static'
      keyboard={false}
      className='spending-onboarding-modal-wrapper'
      dialogClassName='response-artifact-modal spending-onboarding-modal'
      backdropClassName='spending-approval-backdrop'
    >
      {agentImageUrl && (
        <div className='spend-onboarding-agent-avatar'>
          <img src={agentImageUrl} alt='' />
        </div>
      )}
      <Modal.Body>
        <div className='spend-onboarding-content'>
          {explainDeposit && (
            <div className='spend-onboarding-header'>
              <h1 className='spend-onboarding-title'>
                I can reserve things for you
              </h1>
              <p className='spend-onboarding-description'>
                I'm able to make reservations for you. Some reservations need a
                card &mdash; this could be a deposit, a hold, or a no-show
                guarantee depending on the venue.
              </p>
            </div>
          )}
          {!explainDeposit && !agentCardOnly && (
            <div className='spend-onboarding-header'>
              <h1 className='spend-onboarding-title'>I can buy things</h1>
              <p className='spend-onboarding-description'>
                I'm able to buy things for you, or have one of my Dreamsters do
                the same in the real world.
              </p>
              <p className='spend-onboarding-description'>
                It's safe, secure, and you choose how I do it.
              </p>
            </div>
          )}
          {!explainDeposit && agentCardOnly && (
            <div className='spend-onboarding-header'>
              <h1 className='spend-onboarding-title'>
                I can buy things in person
              </h1>
              <p className='spend-onboarding-description'>
                One of my team (Dreamsters) will pay for your item in person.
              </p>
            </div>
          )}

          <div className='spend-onboarding-options'>
            {!explainDeposit && (
              <div className='spend-onboarding-option'>
                <div className='spend-onboarding-option-text'>
                  <h2>My card</h2>
                  {!agentCardOnly && (
                    <p>
                      I have a prepaid credit card. You top it up and I pay. The
                      means you <b>avoid</b> needing to go through further
                      authorization when I buy your item.
                    </p>
                  )}
                  {agentCardOnly && (
                    <p>
                      I have a prepaid credit card. You top it up and I pay. For
                      online purchases you'll be able to add your own card.
                    </p>
                  )}
                </div>
                <div className='spend-onboarding-option-icon'>
                  <AgentCardIllustration />
                </div>
              </div>
            )}

            {(!agentCardOnly || explainDeposit) && (
              <div className='spend-onboarding-option'>
                <div className='spend-onboarding-option-text'>
                  <h2>Your card</h2>
                  <p>
                    {explainDeposit
                      ? "You'll set a card hold limit for each reservation. Your card is stored securely and only used if the venue requires it \u2014 never above the limit you set."
                      : "For online purchases you can add your own card. It's stored securely, and you may need to share a verification code when the item is bought."}
                  </p>
                </div>
                <div className='spend-onboarding-option-icon'>
                  <UserCardIllustration />
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <div className='spend-onboarding-footer'>
        <button className='spend-onboarding-next-btn' onClick={handleNext}>
          Next
        </button>
      </div>
    </Modal>
  );
};

export default SpendingOnboardingModal;
